import { useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { API_ORIGIN } from '../services/api';

/**
 * Subscribe to service request events for a restaurant (new, acknowledged, completed).
 * Used by Manager Dashboard / KDS to show real-time service request notifications.
 */
const useServiceRequestsSocket = (restaurantId, callbacks = {}) => {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!restaurantId) return undefined;

    const socket = io(API_ORIGIN, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true // send the httpOnly auth cookie on the handshake
    });
    socketRef.current = socket;

    socket.on('connect_error', (err) => {
      console.warn('Service requests socket connection rejected:', err.message);
    });

    // Surface transport/engine errors instead of swallowing them silently.
    socket.on('error', (err) => {
      console.error('Service requests socket error:', err && err.message ? err.message : err);
    });

    // After reconnectionAttempts is exhausted the client stops retrying and goes
    // silent — mark disconnected and notify the page so it can warn / fall back.
    // The listener lives on the Manager (socket.io), which can be reused across
    // remounts, so it is removed explicitly in cleanup below.
    const handleReconnectFailed = () => {
      console.error('Service requests socket gave up reconnecting');
      setConnected(false);
      if (callbacksRef.current.onReconnectFailed) {
        callbacksRef.current.onReconnectFailed();
      }
    };
    socket.io.on('reconnect_failed', handleReconnectFailed);

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join_restaurant', String(restaurantId));
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('service_request_created', (payload) => {
      if (callbacksRef.current.onNewRequest) {
        callbacksRef.current.onNewRequest(payload);
      }
    });

    socket.on('service_request_acknowledged', (data) => {
      if (callbacksRef.current.onRequestAcknowledged) {
        callbacksRef.current.onRequestAcknowledged(data);
      }
    });

    socket.on('service_request_completed', (data) => {
      if (callbacksRef.current.onRequestCompleted) {
        callbacksRef.current.onRequestCompleted(data);
      }
    });

    return () => {
      socket.io.off('reconnect_failed', handleReconnectFailed);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [restaurantId]);

  return { socket: socketRef.current, connected };
};

export default useServiceRequestsSocket;
